import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { BsGrid3X3GapFill, BsFillEnvelopeFill } from "react-icons/bs";
import BellIconWithNotifications, {
  NotificationWidget,
} from "../notifications/notificationwidget";
import { IoMap } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";

const apiUrl = process.env.REACT_APP_API_URL;

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [isGlobalSettings, setIsGlobalSettings] = useState(false);

  // Get user from localStorage and determine notification type
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData) {
      setUser(userData);
      // RenewalRole 1 = Global/Admin settings, RenewalRole 2 = User settings
      setIsGlobalSettings(
        userData.RenewalRole === "1" || userData.RenewalRole === 1
      );
      fetchNotificationSettings(userData);
    } else {
      setError("User not found. Please login again.");
      setLoading(false);
    }
  }, []);

  const fetchNotificationSettings = async (userData) => {
    try {
      setLoading(true);
      let url;

      if (userData.RenewalRole === "1" || userData.RenewalRole === 1) {
        // Admin: Fetch global notification settings
        url = `${apiUrl}get-renewal-notification-settings`;
      } else {
        // Regular user: Fetch user-specific notification preferences
        url = `${apiUrl}users/notification-preferences?userId=${userData.id}&userType=user`;
      }

      const token = localStorage.getItem("accessToken");
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform data based on response structure
      if (userData.RenewalRole === "1" || userData.RenewalRole === 1) {
        // Global settings - transform from API structure
        const transformedNotifications = transformGlobalNotifications(
          data.data || []
        );
        setNotifications(transformedNotifications);
      } else {
        // User preferences - transform from API structure
        const transformedNotifications = transformUserPreferences(data.data);
        setNotifications(transformedNotifications);
      }
    } catch (err) {
      console.error("Error fetching notification settings:", err);
      setError("Failed to load notification settings");
      // Fallback to default notifications
      setNotifications(getDefaultNotifications());
    } finally {
      setLoading(false);
    }
  };

  const transformGlobalNotifications = (globalData) => {
    return globalData.map((item) => ({
      id: item.id,
      title: item.notification,
      description: item.description,
      enabled: item.isActive,
      emailNotification: item.allowEmail,
      appNotification: item.allowInApp,
    }));
  };

  const transformUserPreferences = (userData) => {
    // Transform user preferences to match the notification structure
    if (
      userData &&
      userData.preferences &&
      Array.isArray(userData.preferences)
    ) {
      return userData.preferences.map((pref) => ({
        id: pref.notificationId,
        title: pref.notification.notification,
        description: pref.notification.description,
        enabled: pref.isActive,
        emailNotification: pref.allowEmail,
        appNotification: pref.allowInApp,
      }));
    }

    return [];
  };

  const getDefaultNotifications = () => [
    {
      id: 1,
      title: "Data Extraction Completed",
      description:
        "Scheduled job completed successfully. Retrieved data with an expiration date set 90 days from today.",
      enabled: true,
      emailNotification: true,
      appNotification: false,
    },
    {
      id: 2,
      title: "Transmission Initiated",
      description: "Data transfer process started.",
      enabled: true,
      emailNotification: true,
      appNotification: false,
    },
    {
      id: 3,
      title: "Successful Delivery Confirmation",
      description: "Data was successfully transmitted to Surefyre.",
      enabled: true,
      emailNotification: true,
      appNotification: false,
    },
    {
      id: 4,
      title: "Transmission Failure Alert",
      description:
        "Data transmission to Surefyre failed. Please review the error logs for details.",
      enabled: true,
      emailNotification: true,
      appNotification: false,
    },
  ];

  const handleToggleNotification = (id) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id
          ? { ...notification, enabled: !notification.enabled }
          : notification
      )
    );
  };

  const handleToggleEmail = (id) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id
          ? {
              ...notification,
              emailNotification: !notification.emailNotification,
            }
          : notification
      )
    );
  };

  const handleToggleApp = (id) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id
          ? { ...notification, appNotification: !notification.appNotification }
          : notification
      )
    );
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");

      if (isGlobalSettings) {
        // Admin: Update global notification settings
        const updatePromises = notifications.map(async (notification) => {
          const response = await fetch(
            `${apiUrl}update-renewal-notification-setting/${notification.id}`,
            {
              method: "PATCH",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                isActive: notification.enabled,
                allowEmail: notification.emailNotification,
                allowInApp: notification.appNotification,
                lastUpdatedBy: user.id || user.email || "admin", // Track who made the change
              }),
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              `Failed to update ${notification.title}: ${errorData.message}`
            );
          }

          return response.json();
        });

        const results = await Promise.all(updatePromises);
        console.log("Global settings update results:", results);
        alert(
          "Global notification settings saved successfully! User preferences have been automatically synced."
        );
      } else {
        // Regular user: Update user-specific notification preferences
        // Fixed: Use correct URL structure and body format
        const userType = user.role || "user";

        const response = await fetch(
          `${apiUrl}users/${user.id}/${userType}/notification-preferences`,
          {
            method: "PUT", // or "PATCH" depending on your API
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              // Changed from "preferences" to "notificationUpdates" to match API requirement
              notificationUpdates: notifications.map((notification) => ({
                notificationId: notification.id,
                isActive: notification.enabled,
                allowEmail: notification.emailNotification,
                allowInApp: notification.appNotification,
              })),
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        const result = await response.json();
        alert("Notification preferences saved successfully!");
      }
    } catch (err) {
      console.error("Error saving notification settings:", err);
      alert(`Failed to save notification settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values by refetching
    if (user) {
      fetchNotificationSettings(user);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "200px" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="alert alert-danger d-flex align-items-center justify-content-between"
        role="alert"
      >
        <div>
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() => {
            setError(null);
            if (user) {
              fetchNotificationSettings(user);
            }
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-title mb-3">
        <h1 className="mb-0">
          {isGlobalSettings
            ? "Global Notification Settings"
            : "Notification Settings"}
        </h1>
        <div className="d-flex align-items-center gap-3">
          <BellIconWithNotifications />
          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-basic"
                className="rounded-circle border-0 bg-transparent"
              >
                <div
                  className="menu-icon"
                  style={{
                    fontSize: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BsGrid3X3GapFill />
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-menu-custom">
                <Dropdown.Item
                  href="/dashboard"
                  className="dropdown-item-custom"
                >
                  <BsFillEnvelopeFill className="dropdown-icon" /> Quote/Bind
                  Email
                </Dropdown.Item>
                <Dropdown.Item
                  href="/geographical-concentration"
                  className="dropdown-item-custom"
                >
                  <IoMap className="dropdown-icon" /> Geographical Concentration
                </Dropdown.Item>
                <Dropdown.Item
                  href="/renewals-dashboard"
                  className="dropdown-item-custom"
                >
                  <FiRefreshCw className="dropdown-icon" /> Renewals
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="content-body mb-3">
        {isGlobalSettings && (
          <div className="alert alert-info mb-3">
            <i className="bi bi-info-circle me-2"></i>
            These are global notification settings that will apply to all users
            by default.
          </div>
        )}

        <div>
          {notifications.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-bell-slash fs-1 text-muted mb-3"></i>
              <h5 className="text-muted">No notification settings found</h5>
              <p className="text-muted">
                No notifications are currently configured.
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div key={notification.id} className="bg-white p-3 border-bottom">
                <div className="notification-grid">
                  <div className="flex-grow-1">
                    <h6>{notification.title}</h6>
                    <p>{notification.description}</p>
                  </div>
                  <div className="form-check form-switch ms-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`toggle-${notification.id}`}
                      checked={notification.enabled}
                      onChange={() => handleToggleNotification(notification.id)}
                      style={{
                        backgroundColor: notification.enabled ? "#65cc48" : "",
                        borderColor: notification.enabled ? "#65cc48" : "",
                      }}
                    />
                  </div>
                </div>

                {notification.enabled && (
                  <div className="row w-75 options mt-3">
                    <div className="col-md-6">
                      <div className="check-grid p-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`email-${notification.id}`}
                            checked={notification.emailNotification}
                            onChange={() => handleToggleEmail(notification.id)}
                            disabled={!notification.enabled}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`email-${notification.id}`}
                          >
                            Email Notification
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="check-grid p-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`app-${notification.id}`}
                            checked={notification.appNotification}
                            onChange={() => handleToggleApp(notification.id)}
                            disabled={!notification.enabled}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`app-${notification.id}`}
                          >
                            App Notification
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="d-flex gap-3 mt-4">
          <button
            className="btn text-white px-4"
            onClick={handleSave}
            disabled={loading || notifications.length === 0}
            style={{ backgroundColor: "#122066", border: "none" }}
          >
            {loading ? "Saving..." : "Save"}
          </button>
          <button
            className="btn btn-outline-secondary px-4"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default Notifications;
